import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import Delta, { Op } from "quill-delta";
import Quill from "quill";

import { Segment, TaggedOp, TextDiffModalProps } from "../../types";
import TextElement from "@/components_v2/ContentElements/TextElement";
import { TextType } from "@/components_v2/ContentElements/types";
import BaseModal from "@/components_v2/Modal/BaseModal";
import UserAvatar from "@/domains/users/components/UserAvatar";
import { rewriteMarkersForDisplay } from "@/shared/media";

import "../../styles.css";

const highlightLines = (
  segments: Segment[],
  color: string,
  diffType: string,
): Op[] => {
  const taggedOps: TaggedOp[] = [];
  for (const seg of segments) {
    for (const op of seg.ops) {
      taggedOps.push({ op, dirty: seg.dirty });
    }
  }

  const lines: TaggedOp[][] = [];
  let currentLine: TaggedOp[] = [];

  for (const tagged of taggedOps) {
    const { op, dirty } = tagged;
    if (typeof op.insert === "string" && op.insert.includes("\n")) {
      const parts = op.insert.split("\n");
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (part && part.length > 0) {
          currentLine.push({ op: { ...op, insert: parts[i] }, dirty });
        }
        if (i < parts.length - 1) {
          currentLine.push({ op: { ...op, insert: "\n" }, dirty });
          lines.push(currentLine);
          currentLine = [];
        }
      }
    } else {
      currentLine.push(tagged);
    }
  }
  if (currentLine.length > 0) lines.push(currentLine);

  const result: Op[] = [];
  for (const line of lines) {
    const lineDirty = line.some((t) => t.dirty);
    if (lineDirty) {
      const hasContent = line.some((t) =>
        typeof t.op.insert === "string"
          ? t.op.insert !== "\n" && t.op.insert.length > 0
          : true,
      );

      if (!hasContent) {
        result.push({
          insert: "\u00A0",
          attributes: { background: color, diffHighlight: diffType },
        });
      }

      for (const tagged of line) {
        const { op, dirty } = tagged;
        if (!dirty) {
          result.push(op);
          continue;
        }

        const isImageEmbed =
          typeof op.insert === "object" &&
          op.insert !== null &&
          "image" in op.insert;

        if (isImageEmbed) {
          const attrsWithoutBg = { ...(op.attributes ?? {}) };
          delete attrsWithoutBg.background;
          result.push({
            ...op,
            attributes: {
              ...attrsWithoutBg,
              diffHighlight: diffType,
            },
          });
        } else {
          result.push({
            ...op,
            attributes: {
              ...op.attributes,
              background: color,
              diffHighlight: diffType,
            },
          });
        }
      }
    } else {
      for (const { op } of line) {
        result.push(op);
      }
    }
  }
  return result;
};

const consumeOld = (
  length: number,
  oldOps: Op[],
  cursor: { oldOpIndex: number; oldOpOffset: number },
): Op[] => {
  const result: Op[] = [];
  let remaining = length;
  while (remaining > 0 && cursor.oldOpIndex < oldOps.length) {
    const op = oldOps[cursor.oldOpIndex];
    if (!op) return [];
    const opLen = op && typeof op.insert === "string" ? op.insert.length : 1;
    const available = opLen - cursor.oldOpOffset;
    if (available <= remaining) {
      if (cursor.oldOpOffset === 0) {
        result.push({ ...op });
      } else {
        result.push({
          ...op,
          insert: (op.insert as string).slice(cursor.oldOpOffset),
        });
      }
      remaining -= available;
      cursor.oldOpIndex++;
      cursor.oldOpOffset = 0;
    } else {
      if (typeof op.insert === "string") {
        result.push({
          ...op,
          insert: op.insert.slice(
            cursor.oldOpOffset,
            cursor.oldOpOffset + remaining,
          ),
        });
      } else {
        result.push({ ...op });
      }
      cursor.oldOpOffset += remaining;
      remaining = 0;
    }
  }
  return result;
};

const buildRichTextModalDiff = (
  prevValue: string,
  newValue: string,
): { beforeDelta: Delta; afterDelta: Delta } => {
  const tempDiv = document.createElement("div");
  tempDiv.style.display = "none";
  document.body.appendChild(tempDiv);

  try {
    const tempQuill = new Quill(tempDiv);

    tempQuill.clipboard.dangerouslyPasteHTML(
      rewriteMarkersForDisplay(prevValue),
    );
    const oldDelta = new Delta(tempQuill.getContents().ops);

    tempQuill.setText("");
    tempQuill.clipboard.dangerouslyPasteHTML(
      rewriteMarkersForDisplay(newValue),
    );
    const newDelta = new Delta(tempQuill.getContents().ops);

    const diff = oldDelta.diff(newDelta);

    const oldOps = oldDelta.ops;
    const cursor = { oldOpIndex: 0, oldOpOffset: 0 };

    const beforeSegments: Segment[] = [];
    const afterSegments: Segment[] = [];

    diff.ops.forEach((op) => {
      if (op.retain !== undefined && !op.attributes) {
        const chunks = consumeOld(op.retain as number, oldOps, cursor);
        beforeSegments.push({ ops: chunks, dirty: false });
        afterSegments.push({
          ops: chunks.map((c) => ({ ...c })),
          dirty: false,
        });
      } else if (op.retain !== undefined && op.attributes) {
        const chunks = consumeOld(op.retain as number, oldOps, cursor);
        beforeSegments.push({ ops: chunks, dirty: true });
        afterSegments.push({
          ops: chunks.map((c) => ({
            ...c,
            attributes: { ...c.attributes, ...op.attributes },
          })),
          dirty: true,
        });
      } else if (op.delete !== undefined) {
        const chunks = consumeOld(op.delete, oldOps, cursor);
        beforeSegments.push({ ops: chunks, dirty: true });
      } else if (op.insert !== undefined) {
        afterSegments.push({
          ops: [
            {
              insert: op.insert,
              ...(op.attributes ? { attributes: { ...op.attributes } } : {}),
            },
          ],
          dirty: true,
        });
      }
    });

    while (cursor.oldOpIndex < oldOps.length) {
      const chunks = consumeOld(Infinity, oldOps, cursor);
      beforeSegments.push({ ops: chunks, dirty: false });
      afterSegments.push({ ops: chunks.map((c) => ({ ...c })), dirty: false });
    }

    return {
      beforeDelta: new Delta(
        highlightLines(
          beforeSegments,
          "var(--color-danger-overlay)",
          "removed",
        ),
      ),
      afterDelta: new Delta(
        highlightLines(afterSegments, "var(--color-success-overlay)", "added"),
      ),
    };
  } finally {
    document.body.removeChild(tempDiv);
  }
};

const TextDiffModal = ({
  open,
  setOpen,
  title,
  oldValue,
  newValue,
  createdBy,
  createdAt,
}: TextDiffModalProps) => {
  const { t } = useTranslation();
  const { beforeDelta, afterDelta } = useMemo(
    () => buildRichTextModalDiff(oldValue, newValue),
    [oldValue, newValue],
  );

  const modalTitle = (
    <div className="history-rich-text-diff-modal__header flex gap-200 items-center">
      <UserAvatar
        user={createdBy}
        size="extra-large"
        className="flex-shrink-0"
      />

      <div className="history-rich-text-diff-modal__header-text flex flex-col">
        <span className="body-m-regular history-rich-text-diff-modal__heading">
          <span className="body-m-medium">{createdBy.fullName}</span>{" "}
          {t("datablocks.labels.richTextDiffModalMadeChangesTo", {
            fieldTitle: title,
          })}
        </span>

        <span className="body-s-regular history-rich-text-diff-modal__date">
          {DateTime.fromISO(createdAt).toFormat("dd MMMM yyyy")}
        </span>
      </div>
    </div>
  );

  return (
    <BaseModal
      open={open}
      onCancel={() => setOpen(false)}
      title={modalTitle}
      width={1000}
    >
      <div className="history-rich-modal-diff flex gap-250">
        <div className="history-rich-modal-diff__pane ql-snow flex flex-col gap-100 full-width">
          <span className="body-s-medium history-rich-modal-diff__label">
            {t("datablocks.labels.richTextDiffBefore")}
          </span>

          <TextElement value={beforeDelta} type={TextType.RICH_TEXT} />
        </div>

        <div className="history-rich-modal-diff__pane ql-snow flex flex-col gap-100 full-width">
          <span className="body-s-medium history-rich-modal-diff__label">
            {t("datablocks.labels.richTextDiffAfter")}
          </span>

          <TextElement value={afterDelta} type={TextType.RICH_TEXT} />
        </div>
      </div>
    </BaseModal>
  );
};

export default TextDiffModal;
